import React, { useMemo, type KeyboardEvent } from "react";
import { GlobalOutlined, TeamOutlined } from "@ant-design/icons";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { Form, Input, Select, Button } from "../../../../shared";
import { filterByLabel } from "../../../../../utils/selectFilter";
import { useListSegments } from "../../../../../queries/dynamicBonuses/useListSegments";
import { usePamFeature } from "../../../../../hooks/useFeatureFlag";
import { formFieldTooltip } from "./formFieldTooltip";
import styles from "../../stylesDynamicBonuses.module.css";

type Props = {
  onNext?: () => void;
  onPrev?: () => void;
  /** When false, omit the step footer (combined Details screen owns Next). */
  showFooter?: boolean;
  /** Active/draft/paused campaigns keep audience selection locked. */
  rulesLocked?: boolean;
};

type AudienceMode = "all" | "segment";

type AudienceOptionCardProps = {
  value: AudienceMode;
  selected: boolean;
  disabled: boolean;
  icon: React.ReactNode;
  title: string;
  description: string;
  onSelect: (value: AudienceMode) => void;
  children?: React.ReactNode;
};

/**
 * Compact audience option. Not a `<button>` so the Segment card can host a
 * nested Select without invalid interactive nesting.
 */
const AudienceOptionCard: React.FC<AudienceOptionCardProps> = ({
  value,
  selected,
  disabled,
  icon,
  title,
  description,
  onSelect,
  children,
}) => {
  const handleActivate = () => {
    if (disabled) return;
    onSelect(value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(value);
    }
  };

  return (
    <div
      role="radio"
      tabIndex={disabled ? -1 : 0}
      aria-label={title}
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      className={[
        styles.audienceOptionCard,
        selected ? styles.audienceOptionCardSelected : "",
        disabled ? styles.audienceOptionCardDisabled : "",
      ]
        .join(" ")
        .trim()}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.audienceOptionHeader}>
        <span className={styles.audienceOptionIcon} aria-hidden="true">
          {icon}
        </span>
        <span className={styles.audienceOptionTitle}>{title}</span>
        <span className={styles.audienceOptionDescription}>{description}</span>
        <span
          className={styles.audienceOptionRadio}
          aria-hidden="true"
          data-selected={selected || undefined}
        >
          <span className={styles.audienceOptionRadioDot} />
        </span>
      </div>
      {children ? (
        <div
          className={styles.audienceOptionBody}
          data-testid="audience-option-body"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

/** Step 2 — everyone, or a user segment mapped to a user_in_segment condition. */
const AudienceStep: React.FC<Props> = ({
  onNext,
  onPrev,
  showFooter = true,
  rulesLocked = false,
}) => {
  const { data: segments, isLoading: segmentsLoading } = useListSegments();
  const { isEnabled: dynamicBonusesBetaMode } = usePamFeature(
    "dynamicBonusesBetaMode",
  );
  const segmentAudienceEnabled = dynamicBonusesBetaMode === true;
  const segmentAudienceLocked = rulesLocked || !segmentAudienceEnabled;

  const segmentOptions = useMemo(
    () =>
      (segments ?? [])
        .filter((s) => s.status === "active")
        .map((s) => ({ label: s.name, value: s.segment_id })),
    [segments],
  );

  return (
    <div
      className={rulesLocked ? styles.readOnlyFields : undefined}
      ref={(element) => {
        if (rulesLocked) {
          element?.setAttribute("inert", "");
        } else {
          element?.removeAttribute("inert");
        }
      }}
      aria-disabled={rulesLocked || undefined}
      data-testid={rulesLocked ? "locked-campaign-audience" : undefined}
    >
      <Form.Item
        label={AppStrings.PAM_DYNAMIC_BONUS_SELECT_AUDIENCE_LABEL}
        tooltip={formFieldTooltip(
          AppStrings.PAM_DYNAMIC_BONUS_SELECT_AUDIENCE_TOOLTIP,
        )}
        className={styles.audienceFormItem}
      >
        {/* Registers audience_mode so setFieldValue notifies watchers; the
            cards below are the visible control. */}
        <Form.Item name="audience_mode" hidden>
          <Input />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldValue }) => {
            const mode: AudienceMode =
              (getFieldValue("audience_mode") as AudienceMode | undefined) ??
              "all";
            // Segment is a beta authoring surface — hide it on create when
            // beta mode is off. Keep the card if a hydrated campaign already
            // uses a segment so edit does not look like All users.
            const showSegmentCard =
              segmentAudienceEnabled || mode === "segment";
            return (
              <div
                role="radiogroup"
                aria-label={AppStrings.PAM_DYNAMIC_BONUS_SELECT_AUDIENCE_LABEL}
                className={[
                  styles.audienceOptionGrid,
                  showSegmentCard ? "" : styles.audienceOptionGridSingle,
                ]
                  .join(" ")
                  .trim()}
              >
                <AudienceOptionCard
                  value="all"
                  selected={mode === "all"}
                  disabled={rulesLocked}
                  icon={<GlobalOutlined />}
                  title={AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_ALL_USERS}
                  description={
                    AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_ALL_USERS_DESCRIPTION
                  }
                  onSelect={(v) => setFieldValue("audience_mode", v)}
                />
                {showSegmentCard ? (
                  <AudienceOptionCard
                    value="segment"
                    selected={mode === "segment"}
                    disabled={segmentAudienceLocked}
                    icon={<TeamOutlined />}
                    title={AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_SEGMENT}
                    description={
                      AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_SEGMENT_DESCRIPTION
                    }
                    onSelect={(v) => setFieldValue("audience_mode", v)}
                  >
                    {mode === "segment" ? (
                      <Form.Item
                        name="segment_id"
                        rules={[
                          {
                            required: true,
                            message:
                              AppStrings.PAM_DYNAMIC_BONUS_SELECT_SEGMENT_REQUIRED,
                          },
                        ]}
                        className={styles.audienceSegmentSelect}
                      >
                        <Select
                          placeholder={
                            segmentsLoading
                              ? AppStrings.PAM_DYNAMIC_BONUS_LOADING_SEGMENTS
                              : AppStrings.PAM_DYNAMIC_BONUS_SELECT_SEGMENT
                          }
                          aria-label={AppStrings.PAM_DYNAMIC_BONUS_SELECT_SEGMENT}
                          options={segmentOptions}
                          loading={segmentsLoading}
                          showSearch
                          filterOption={filterByLabel}
                          disabled={segmentAudienceLocked}
                        />
                      </Form.Item>
                    ) : null}
                  </AudienceOptionCard>
                ) : null}
              </div>
            );
          }}
        </Form.Item>
      </Form.Item>

      {showFooter && (
        <div className={styles.stepFooter}>
          <Button intent="neutral" onClick={onPrev}>
            Previous
          </Button>
          <Button intent="primary" onClick={onNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default AudienceStep;
